import { useState, useCallback, useRef, useMemo } from 'react';
import './California.scss';
import californiaZips from '../../data/california_zips.json';
import customerinfo from "../../data/customerinfo";
import Map, {
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Layer,
  Source,
  MapRef
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Header } from '../../components/Header';
import { ActionButtons } from '../../components/ActionButtons';
import { CardButton, TextInput } from '@fsg/gui-bits';

function California({ publicView = false }) {
  const [selectedZip, setSelectedZip] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const map = useRef<MapRef>(null);

  document.title = `FSG ${publicView ? "(Public)" : ""} - California Service Areas`;

  // Get unique zip codes with division data for the sidebar
  const zipList = useMemo(() => {
    const zips: Array<{zip: string, name: string, division: string}> = [];
    californiaZips.features.forEach((feature: any) => {
      if (feature.properties.zip && feature.properties.division) {
        zips.push({
          zip: feature.properties.zip,
          name: feature.properties.name,
          division: feature.properties.division
        });
      }
    });
    // Sort by zip code
    return zips.sort((a, b) => a.zip.localeCompare(b.zip));
  }, []);

  // Filter zips based on search term
  const filteredZips = useMemo(() => {
    if (!searchTerm) return zipList;
    const term = searchTerm.toLowerCase();
    return zipList.filter(z =>
      z.zip.includes(term) ||
      z.name.toLowerCase().includes(term)
    );
  }, [zipList, searchTerm]);

  // Handle map click
  const handleMapClick = useCallback((event: any) => {
    if (!map.current) return;

    const features = map.current.getMap().queryRenderedFeatures(event.point, {
      layers: ['zip-fills-12', 'zip-fills-other']
    });

    if (features && features.length > 0) {
      const feature = features[0];
      setSelectedZip(feature.properties);
    }
  }, []);

  // Layer styles for division 12 (primary)
  const division12FillLayer: any = {
    id: 'zip-fills-12',
    type: 'fill' as const,
    source: 'zips',
    filter: ['==', ['get', 'division'], '12'],
    paint: {
      'fill-color': '#DAE8FF',
      'fill-opacity': 0.6
    }
  };

  const division12LineLayer: any = {
    id: 'zip-lines-12',
    type: 'line' as const,
    source: 'zips',
    filter: ['==', ['get', 'division'], '12'],
    paint: {
      'line-color': '#335EEE',
      'line-width': 2
    }
  };

  // Layer styles for other divisions (secondary)
  const divisionOtherFillLayer: any = {
    id: 'zip-fills-other',
    type: 'fill' as const,
    source: 'zips',
    filter: ['!=', ['get', 'division'], '12'],
    paint: {
      'fill-color': '#FDF2C4',
      'fill-opacity': 0.6
    }
  };

  const divisionOtherLineLayer: any = {
    id: 'zip-lines-other',
    type: 'line' as const,
    source: 'zips',
    filter: ['!=', ['get', 'division'], '12'],
    paint: {
      'line-color': '#E99C00',
      'line-width': 2
    }
  };

  // Fly to zip code
  const flyToZip = useCallback((zip: string) => {
    const feature = californiaZips.features.find((f: any) => f.properties.zip === zip);
    if (feature && map.current) {
      // Calculate center of the polygon
      const coords = feature.geometry.coordinates as any;
      let allCoords: any[] = [];

      if (feature.geometry.type === 'Polygon') {
        allCoords = coords[0] as any[];
      } else if (feature.geometry.type === 'MultiPolygon') {
        allCoords = coords[0][0] as any[];
      }

      if (allCoords.length > 0) {
        const lngs = allCoords.map((c: any) => c[0]);
        const lats = allCoords.map((c: any) => c[1]);
        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;

        map.current.flyTo({
          center: [centerLng, centerLat],
          zoom: 11,
          duration: 1000,
        });

        setSelectedZip(feature.properties);
      }
    }
  }, []);

  return (
    <div>
      <Header customer="California Service Areas" />
      <div className="dashboard-content">
        <ActionButtons customerGuid={customerinfo.california.guid} publicView={publicView} />

        <div className="map-container">
          <div className="site-list">
            <div className="site-search">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by zip code or area name"
              />
            </div>
            <div className="site-list-list">
              {filteredZips.map((item) => {
                const isPrimary = item.division === '12';
                return (
                  <CardButton
                    key={item.zip}
                    className="site-listing"
                    onClick={() => flyToZip(item.zip)}
                    style={{
                      borderLeft: `4px solid ${isPrimary ? '#335EEE' : '#E99C00'}`,
                    }}
                  >
                    <h4>{item.zip}</h4>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>{item.name}</div>
                    <div style={{
                      fontSize: '0.8rem',
                      marginTop: '0.25rem',
                      color: isPrimary ? '#335EEE' : '#E99C00',
                      fontWeight: 600
                    }}>
                      {isPrimary ? 'Primary' : 'Secondary'}
                    </div>
                  </CardButton>
                );
              })}
            </div>
          </div>

          <Map
            ref={map}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={{
              longitude: -119.4,
              latitude: 37.0,
              zoom: 5.5,
            }}
            style={{ width: "100%", height: "80vh", marginTop: "1rem" }}
            mapStyle="mapbox://styles/mapbox/light-v11"
            onClick={handleMapClick}
            cursor="pointer"
          >
            <GeolocateControl position="top-left" />
            <FullscreenControl position="top-left" />
            <NavigationControl position="top-left" />
            <ScaleControl />

            <Source
              id="zips"
              type="geojson"
              data={californiaZips as any}
            >
              <Layer {...division12FillLayer} />
              <Layer {...division12LineLayer} />
              <Layer {...divisionOtherFillLayer} />
              <Layer {...divisionOtherLineLayer} />
            </Source>
          </Map>

          {selectedZip && (
            <div className="zip-info-panel">
              <button
                className="close-button"
                onClick={() => setSelectedZip(null)}
              >
                ×
              </button>
              <h2>Zip Code: {selectedZip.zip}</h2>
              <p className="area-name">{selectedZip.name}</p>
              <div className="service-area">
                <strong>Service Area:</strong>{' '}
                <span className={selectedZip.division === '12' ? 'primary' : 'secondary'}>
                  {selectedZip.division === '12' ? 'Primary' : 'Secondary'}
                </span>
              </div>
              {selectedZip.population > 0 && (
                <div className="population">
                  <strong>Population:</strong> {selectedZip.population.toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default California;
